import { SubmitButton } from 'components/common/buttons/SubmitButton';
import ErrorMessage from 'components/common/ErrorMessage';
import { ProfilePicture } from 'components/common/ProfilePicture';
import React, { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form';
import PhoneInput from 'components/common/PhoneInput';
import { phoneDigitsOrEmpty } from 'helpers/phoneDigits';
import { useDispatch, useSelector } from 'react-redux';
import { cdn } from 'constants/common';
import { setError } from 'slices/userSlice';
import toast from 'react-hot-toast';
import FullPageLoader from 'components/common/FullPageLoader';
import Api from 'services/api';
import { CustomSwitch } from 'components/common/Switch';
import { interpreterProfileSelector } from 'slices/interpreter/profileSlice';
import { fetchInterpreterProfile } from 'slices/interpreter/profileSlice';
import { updateInterpreterProfile } from 'slices/interpreter/profileSlice';

const ALLOWED_TYPES = ['image/png', 'image/jpeg'];
const MAX_SIZE_BYTES = 2 * 1024 * 1024;

/** A field the interpreter can see but not change — maintained by court staff. */
const ReadOnlyValue = ({ value }) => (
    <div className="input-in type_inp" style={{ width: '100%', background: '#eef2f6', color: '#6c889c', display: 'flex', alignItems: 'center', cursor: 'not-allowed' }}>
        {value || '—'}
    </div>
);

const AdminManagedNote = () => (
    <p className="para2" style={{ fontSize: 13, color: '#809FB8' }}>
        Some details below are maintained by the court and can't be edited here. To correct your name,
        address, languages or payment details, please contact the interpreter coordinator.<br/><br/>
    </p>
);

const ShowProfile = ({ item, handleEdit }) => {

    const InfoRow = ({ label, value }) => (
        <tr>
            <td className='col-3'><strong>{label}</strong></td>
            <td className='col-9'>{value || 'N/A'}</td>
        </tr>
    );


    return (
        <div className='container_main mt-4 profile-container'>
            <div className="profile-header">
                <div className="profile-image">
                    <ProfilePicture imageUrl={item?.user?.image ? (cdn + item?.user?.image) : '/images/default-person.png'} />
                </div>
                <div className="profile-info">
                    <h1 className='text-capitalize'>{item?.user?.name}</h1>
                    <p>{item?.address}</p>
                    <div className="edit_prof">
                        <button type='button' className='main_button' onClick={handleEdit}>
                            <i className="fa-solid fa-pen-to-square"></i>&nbsp;&nbsp;
                            <span>Edit Profile</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="profile-section">
                <h2>Personal Information</h2>
                <table>
                    <InfoRow label="First Name:" value={item?.first_name} />
                    <InfoRow label="Last Name:" value={item?.last_name} />
                    <InfoRow label="AOC Roster Name:" value={item?.display_name} />
                    <InfoRow label="Email:" value={item?.user?.email} />
                    <InfoRow label="Username:" value={item?.user?.username} />
                    <InfoRow label="Phone 1:" value={item?.phone_1} />
                    <InfoRow label="Phone 2:" value={item?.phone_2} />
                    <InfoRow label="Phone 3:" value={item?.phone_3} />
                    <InfoRow label="State:" value={item?.state?.name} />
                    <InfoRow label="City:" value={item?.city_id} />
                    <InfoRow label="Zip Code:" value={item?.zip_code} />
                    <InfoRow label="Address:" value={item?.address} />
                    <InfoRow label="Vendor Number:" value={item?.vendor_number} />
                    <InfoRow label="Vendor Site Code:" value={item?.vendor_site_code} />
                    {item?.vendor_site_code === 'Paper Check' && (
                        <InfoRow label="Paper Check Payee:" value={item?.paper_check_payee} />
                    )}
                    <InfoRow label="Translator Default Rate:" value={item?.translator_default_rate} />
                    <InfoRow label="Languages:" value={item?.languages?.map(f => f?.language?.name).join(', ')} />
                    <InfoRow label="Receive Job Invitation Emails:" value={item?.job_email_notifications === 0 ? 'No' : 'Yes'} />
                    <InfoRow label="Receive Job Related Emails:" value={item?.assigned_job_email_notifications === 0 ? 'No' : 'Yes'} />
                    <InfoRow label="Role:" value="Interpreter" />
                </table>
            </div>

            <UnavailableDatesCard initialDates={item?.unavailable_dates} />

        </div>
    );
};

/** Self-service "Dates I am not available for work" — saves immediately per add/delete. */
const UnavailableDatesCard = ({ initialDates }) => {
    const [dates, setDates] = useState([]);
    const [newStart, setNewStart] = useState('');
    const [newEnd, setNewEnd] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDates(Array.isArray(initialDates) ? initialDates : []);
    }, [initialDates]);

    const handleAdd = async () => {
        if (!newStart || !newEnd) {
            toast.error('Please select both start and end dates.', { className: 'errorToast' });
            return;
        }
        if (newEnd < newStart) {
            toast.error('End date cannot be before start date.', { className: 'errorToast' });
            return;
        }
        setSaving(true);
        const res = await Api.addMyUnavailableDate(newStart, newEnd);
        setSaving(false);
        if (res?.status >= 200 && res?.status < 300) {
            setDates(prev => [...prev, res?.data?.data]);
            setNewStart('');
            setNewEnd('');
            toast.success('Unavailable dates added.', { className: 'successToast' });
        } else {
            toast.error(res?.data?.message || 'Could not add dates.', { className: 'errorToast' });
        }
    };

    const handleDelete = async (id) => {
        setSaving(true);
        const res = await Api.deleteMyUnavailableDate(id);
        setSaving(false);
        if (res?.status >= 200 && res?.status < 300) {
            setDates(prev => prev.filter(d => d.id !== id));
            toast.success('Unavailable dates removed.', { className: 'successToast' });
        } else {
            toast.error(res?.data?.message || 'Could not remove dates.', { className: 'errorToast' });
        }
    };

    return (
        <div style={{ marginTop: 24 }}>
            <br />
            <h3 style={{ color: '#6c889c' }} className='fs-5 fw-bold text-center mt-3'>Dates I am not available for work:</h3>
            <p style={{ color: '#809FB8', fontSize: 13, marginTop: 4, textAlign: 'center' }}>
                You will not be invited to jobs scheduled within these dates.
            </p>

            <div className="addinterform custom-table-wrapper card_table mt-4">
                <table className="custom-table table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dates.map((d, i) => (
                            <tr key={d.id}>
                                <td>{i + 1}</td>
                                <td>{d.start_date}</td>
                                <td>{d.end_date}</td>
                                <td className="d-flex justify-content-end">
                                    <button
                                        className="table_delete"
                                        type="button"
                                        disabled={saving}
                                        onClick={() => handleDelete(d.id)}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}

                        <tr>
                            <td>—</td>
                            <td>
                                <div className="row-primary m-0 input-image border-0" style={{ padding: '0px' }}>
                                    <input type="date" className="input-in type_inp h-100" value={newStart} onChange={e => setNewStart(e.target.value)} style={{ height: '45px' }} />
                                    <img alt="celender Icon" className="custom-icon" src="/images/celender.png" />
                                </div>
                            </td>
                            <td>
                                <div className="row-primary m-0 input-image border-0" style={{ padding: '0px' }}>
                                    <input type="date" className="input-in type_inp h-100" value={newEnd} onChange={e => setNewEnd(e.target.value)} style={{ height: '45px' }} />
                                    <img alt="celender Icon" className="custom-icon" src="/images/celender.png" />
                                </div>
                            </td>
                            <td className="d-flex justify-content-end">
                                <button className="main_button" type="button" disabled={saving} onClick={handleAdd}>Add</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};


const Profile = () => {
    const dispatch = useDispatch();
    const { handleSubmit, watch, formState: { errors }, control, setValue } = useForm();
    const { interpreterProfile: item, error, loader } = useSelector(interpreterProfileSelector);
    const [activeEdit, setActiveEdit] = useState(false);
    const [localImage, setLocalImage] = React.useState('');
    const [imageError, setImageError] = useState('');
    const [removeImage, setRemoveImage] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        Promise.allSettled([dispatch(fetchInterpreterProfile())])
    }, [])

    // Only the editable fields are registered with the form — the admin-managed ones are
    // rendered as read-only values straight from `item`.
    useEffect(() => {
        setValue('email', item?.user?.email || '');
        setValue('username', item?.user?.username || '');
        setValue('password', '');
        setValue('password_confirmation', '');
        // Same scrub the phone widget applies for display, so the submitted value can never be a
        // legacy text annotation the API's max:20 would reject (see helpers/phoneDigits).
        setValue('phone_1', phoneDigitsOrEmpty(item?.phone_1));
        setValue('phone_2', phoneDigitsOrEmpty(item?.phone_2));
        setValue('phone_3', phoneDigitsOrEmpty(item?.phone_3));
        setValue('job_email_notifications', item?.job_email_notifications === 0 ? 0 : 1);
        setValue('assigned_job_email_notifications', item?.assigned_job_email_notifications === 0 ? 0 : 1);
        dispatch(setError({}));
    }, [item, activeEdit, setValue])


    const submitCallback = (status, message) => {
        if (status) {
            toast.success(message, { className: 'successToast' });
            dispatch(fetchInterpreterProfile());
            handleCancelEdit();
        } else {
            toast.error(message, { className: 'errorToast' });
        }
    };

    const onSubmit = (data) => {
        dispatch(setError({}));

        const formData = new FormData();
        // Only what the interpreter is allowed to change. Name, AOC roster name, address,
        // state/city/zip, vendor & pay fields and languages are admin-managed and are
        // rejected by the API even if sent.
        const scalarFields = [
            'email', 'username', 'phone_1', 'phone_2', 'phone_3',
        ];
        scalarFields.forEach(k => formData.set(k, data[k] ?? ''));
        formData.set('job_email_notifications', data.job_email_notifications == 1 ? 1 : 0);
        formData.set('assigned_job_email_notifications', data.assigned_job_email_notifications == 1 ? 1 : 0);

        if (data.password) formData.set('password', data.password);
        if (data.password_confirmation) formData.set('password_confirmation', data.password_confirmation);

        if (data.image && data.image instanceof File) {
            formData.append('image', data.image);
        }
        if (removeImage) {
            formData.set('remove_image', '1');
        }

        dispatch(updateInterpreterProfile(formData, submitCallback));
    };

    function handleEdit() {
        dispatch(fetchInterpreterProfile());
        setActiveEdit(true);
    }
    function handleCancelEdit() {
        dispatch(fetchInterpreterProfile());
        setActiveEdit(false);
        setLocalImage('');
        setImageError('');
        setRemoveImage(false);
    }

    const handleImageChange = (file, fieldOnChange) => {
        setImageError('');
        if (!file) return;
        if (!ALLOWED_TYPES.includes(file.type)) {
            setImageError('Only PNG, JPG and JPEG files are allowed.');
            return;
        }
        if (file.size > MAX_SIZE_BYTES) {
            setImageError('Image must be 2 MB or smaller.');
            return;
        }
        const blobUrl = URL.createObjectURL(file);
        setLocalImage(blobUrl);
        setRemoveImage(false);
        fieldOnChange(file);
    };

    const handleRemoveImage = (fieldOnChange) => {
        setLocalImage('');
        setImageError('');
        setRemoveImage(true);
        fieldOnChange(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };


    // (The states dropdown was removed — state/city/zip are admin-managed and now render
    // read-only from the loaded profile.)




    return (
        <div className="content">
            <div className="row_main_head">
                <h2 className="main_heading">Profile Page</h2>
            </div>
            {activeEdit === false ?
                <ShowProfile item={item} handleEdit={handleEdit} />
                :
                <div className='container_main mt-4'>
                    <form className='addinterform card_table' style={{ background: 'white', margin: 'auto', padding: '40px', maxWidth: '1440px', marginTop: '22px', marginBottom: '70px' }} onSubmit={handleSubmit(onSubmit)}>

                        {/* Profile Picture */}
                        <div className="row-primary">
                            <h2>Profile Picture</h2>
                            <div className='Upload_wrapper'>
                                {(localImage || item?.user?.image) && !removeImage &&
                                    <div className='image_preview'>
                                        <ProfilePicture imageUrl={localImage || (cdn + item?.user?.image) || '/images/default-person.png'} />
                                    </div>
                                }
                                <div className='w-100'>
                                    <Controller name="image" control={control} render={({ field }) => (
                                        <>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept=".png,.jpg,.jpeg"
                                                style={{ display: 'none' }}
                                                onChange={e => handleImageChange(e.target.files?.[0], field.onChange)}
                                            />
                                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                <button type='button' onClick={() => fileInputRef.current?.click()}>
                                                    <i className="fa-solid fa-camera"></i>&nbsp;&nbsp;
                                                    <span>Upload New</span>
                                                </button>
                                                {(localImage || item?.user?.image) && !removeImage && (
                                                    <button type='button' className='Cancle_button' style={{ padding: '8px 16px' }} onClick={() => handleRemoveImage(field.onChange)}>
                                                        <i className="fa-solid fa-trash"></i>&nbsp;&nbsp;
                                                        <span>Remove Picture</span>
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )} />
                                    {imageError && <div style={{ marginBottom: 10 }}><ErrorMessage text={imageError} /></div>}
                                    {error?.errors?.image?.map((err, i) => (
                                        <div key={i} style={{ marginBottom: 10 }}><ErrorMessage text={err} /></div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Name and AOC Roster Name are maintained by court staff */}
                        <AdminManagedNote />

                        <div className="row-primary">
                            <h2>First Name</h2>
                            <ReadOnlyValue value={item?.first_name} />
                            <h3 className="text-nowrap">Last Name</h3>
                            <ReadOnlyValue value={item?.last_name} />
                        </div>

                        <div className="row-primary">
                            <h2>AOC Roster Name</h2>
                            <ReadOnlyValue value={item?.display_name} />
                        </div>

                        {/* Email */}
                        <div className="row-primary">
                            <h2>Email *</h2>
                            <div className="w-100">
                                <Controller name="email" control={control}
                                    rules={{
                                        required: 'Email is required',
                                        pattern: { value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: 'Invalid email' },
                                    }}
                                    render={({ field }) => (
                                        <input className={`input-in type_inp ${error?.errors?.email ? 'border-red' : ''}`} type="email" placeholder="Email Address" {...field} />
                                    )} />
                                {errors?.email?.message && <div style={{ marginBottom: 10 }}><ErrorMessage text={errors.email.message} /></div>}
                                {error?.errors?.email?.map((err, i) => <div key={i} style={{ marginBottom: 10 }}><ErrorMessage text={err} /></div>)}
                            </div>
                        </div>

                        {/* Username */}
                        <div className="row-primary">
                            <h2>Username</h2>
                            <div className="w-100">
                                <Controller name="username" control={control}
                                    render={({ field }) => (
                                        <input className="input-in type_inp" type="text" placeholder="Username" {...field} />
                                    )} />
                                {error?.errors?.username?.map((err, i) => <div key={i} style={{ marginBottom: 10 }}><ErrorMessage text={err} /></div>)}
                            </div>
                        </div>

                        {/* Password + Confirm Password */}
                        <div className="row-primary">
                            <h2>Password</h2>
                            <div className="w-100">
                                <Controller name="password" control={control}
                                    render={({ field }) => (
                                        <input className="input-in type_inp" type="password" placeholder="Password (leave blank to keep current)" {...field} />
                                    )} />
                                {errors?.password?.message && <div style={{ marginBottom: 10 }}><ErrorMessage text={errors.password.message} /></div>}
                                {error?.errors?.password?.map((err, i) => <div key={i} style={{ marginBottom: 10 }}><ErrorMessage text={err} /></div>)}
                            </div>
                            <h3 className="text-nowrap">Confirm Password</h3>
                            <div style={{ width: '100%' }}>
                                <Controller name="password_confirmation" control={control}
                                    rules={{
                                        validate: value => !watch('password') || value === watch('password') || 'Passwords do not match',
                                    }}
                                    render={({ field }) => (
                                        <input className="input-in type_inp" type="password" placeholder="Confirm Password" {...field} />
                                    )} />
                                {errors?.password_confirmation?.message && <div style={{ marginBottom: 10 }}><ErrorMessage text={errors.password_confirmation.message} /></div>}
                            </div>
                        </div>

                        {/* Phone 1 */}
                        <div className="row-primary">
                            <h2>Phone Number 1 *</h2>
                            <div className="w-100">
                                <Controller name="phone_1" control={control} rules={{ required: 'Phone number is required' }}
                                    render={({ field }) => (
                                        <PhoneInput className="input-in type_inp" type="tel" placeholder="Phone number 1" {...field} />
                                    )} />
                                {errors?.phone_1?.message && <div style={{ marginBottom: 10 }}><ErrorMessage text={errors.phone_1.message} /></div>}
                                {error?.errors?.phone_1?.map((err, i) => <div key={i} style={{ marginBottom: 10 }}><ErrorMessage text={err} /></div>)}
                            </div>
                        </div>

                        {/* Phone 2 + Phone 3 */}
                        <div className="row-primary">
                            <h2>Phone Number 2</h2>
                            <div className="w-100">
                                <Controller name="phone_2" control={control}
                                    render={({ field }) => (
                                        <PhoneInput className="input-in type_inp" type="tel" placeholder="Phone number 2" {...field} />
                                    )} />
                            </div>
                            <h3 className="text-nowrap">Phone Number 3</h3>
                            <div className="w-100">
                                <Controller name="phone_3" control={control}
                                    render={({ field }) => (
                                        <PhoneInput className="input-in type_inp" type="tel" placeholder="Phone number 3" {...field} />
                                    )} />
                            </div>
                        </div>

                        {/* Address, and vendor/payment details — all admin-managed */}
                        <div className="row-primary">
                            <h2>Address</h2>
                            <ReadOnlyValue value={item?.address} />
                        </div>

                        <div className="row-primary">
                            <h2>State</h2>
                            <ReadOnlyValue value={item?.state?.name} />
                            <h3 className="text-nowrap">City</h3>
                            <ReadOnlyValue value={item?.city_id} />
                            <h3 className="text-nowrap">Zip Code</h3>
                            <ReadOnlyValue value={item?.zip_code} />
                        </div>

                        <div className="row-primary">
                            <h2>Vendor Number:</h2>
                            <ReadOnlyValue value={item?.vendor_number} />
                            <h3 className="text-nowrap">Vendor Site Code:</h3>
                            <ReadOnlyValue value={item?.vendor_site_code} />
                        </div>

                        {item?.vendor_site_code === 'Paper Check' && (
                            <div className="row-primary">
                                <h2>Paper Check Payee:</h2>
                                <ReadOnlyValue value={item?.paper_check_payee} />
                            </div>
                        )}


                        {/* Job-broadcast email opt-out — you can still view and claim jobs in the portal */}
                        <div className="row-primary" style={{ alignItems: 'center' }}>
                            <h2 className='m-0'>Receive Job Invitation Emails</h2>
                            <div style={{ width: '100%' }}>
                                <Controller name="job_email_notifications" control={control}
                                    render={({ field }) => (
                                        <CustomSwitch
                                            labelText="active"
                                            checked={field.value == 1}
                                            handleChange={(checked) => field.onChange(checked ? 1 : 0)}
                                        />
                                    )} />
                                <div style={{ fontSize: 12, color: '#809FB8', marginTop: 4 }}>
                                    Turn off to stop receiving emails about new open jobs. You can still see and claim jobs from your dashboard.
                                </div>
                            </div>
                        </div>

                        {/* Assigned-job email opt-out */}
                        <div className="row-primary" style={{ alignItems: 'center' }}>
                            <h2 className='m-0'>Receive Job Related Emails</h2>
                            <div style={{ width: '100%' }}>
                                <Controller name="assigned_job_email_notifications" control={control}
                                    render={({ field }) => (
                                        <CustomSwitch
                                            labelText="active"
                                            checked={field.value == 1}
                                            handleChange={(checked) => field.onChange(checked ? 1 : 0)}
                                        />
                                    )} />
                                <div style={{ fontSize: 12, color: '#809FB8', marginTop: 4 }}>
                                    Turn off to stop receiving emails about jobs you are assigned to — reschedules, updates, case changes, and reminders.
                                </div>
                            </div>
                        </div>

                        <div className="Form-footer">
                            <button type='button' className='Cancle_button' onClick={handleCancelEdit}>Cancel</button>
                            <SubmitButton btnText="Update Profile" loader={loader} />
                        </div>
                    </form>
                </div>
            }
            {loader && <FullPageLoader loader={loader} />}
        </div>
    )
}

export default Profile
